/**
 * Client-side image downscaling/recompression used before an image attachment
 * is read into the request body. Anthropic rejects images over 5 MB or larger
 * than 8000x8000 px, so images that breach either bound are re-encoded toward
 * those limits here. Images that still don't fit are rejected by the caller.
 *
 * Re-encoding rasterizes the image: a GIF loses animation (first frame only)
 * and a PNG with transparency may be flattened when it falls back to JPEG.
 * This only runs when the image would otherwise be rejected by the model, so
 * the lossy trade-off is preferable to a hard failure.
 */

export interface CompressImageOptions {
  /** Target maximum byte size of the encoded result. */
  maxBytes: number;
  /** Maximum allowed length of the longest edge, in pixels. */
  maxDimension: number;
}

export interface CompressImageResult {
  file: File;
  /** True when the returned file was re-encoded (size/dimensions changed). */
  wasCompressed: boolean;
}

/** Quality steps tried for lossy re-encoding, largest first. */
const QUALITY_STEPS = [0.92, 0.85, 0.75, 0.65, 0.55, 0.45] as const;
/** Dimension scale-downs applied when quality alone can't hit the byte target. */
const DIMENSION_STEPS = [1, 0.75, 0.5] as const;

async function loadBitmap(
  file: File,
): Promise<{ width: number; height: number; source: CanvasImageSource; release: () => void }> {
  if (typeof createImageBitmap === 'function') {
    const bitmap = await createImageBitmap(file);
    return {
      width: bitmap.width,
      height: bitmap.height,
      source: bitmap,
      release: () => bitmap.close(),
    };
  }
  // Fallback for environments without createImageBitmap.
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('Failed to decode image'));
      el.src = url;
    });
    return {
      width: img.naturalWidth,
      height: img.naturalHeight,
      source: img,
      release: () => URL.revokeObjectURL(url),
    };
  } catch (err) {
    URL.revokeObjectURL(url);
    throw err;
  }
}

function canvasToBlob(
  canvas: HTMLCanvasElement,
  mimeType: string,
  quality: number,
): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), mimeType, quality);
  });
}

/**
 * Returns the image re-encoded to fit `maxBytes`/`maxDimension`, the original
 * untouched when it already fits, or `null` when it cannot be made to fit.
 */
export async function compressImageToLimit(
  file: File,
  opts: CompressImageOptions,
): Promise<CompressImageResult | null> {
  const { maxBytes, maxDimension } = opts;

  let bitmap: Awaited<ReturnType<typeof loadBitmap>>;
  try {
    bitmap = await loadBitmap(file);
  } catch {
    // Can't decode it as an image; let the caller fall back to its own checks.
    return file.size <= maxBytes ? { file, wasCompressed: false } : null;
  }

  const { width, height, source, release } = bitmap;
  const withinDimensions = width <= maxDimension && height <= maxDimension;
  if (file.size <= maxBytes && withinDimensions) {
    release();
    return { file, wasCompressed: false };
  }

  // Prefer WebP (smaller, keeps alpha) and fall back to JPEG when unsupported.
  const outputMime = canSupportWebp() ? 'image/webp' : 'image/jpeg';
  const baseName = file.name.replace(/\.[^./\\]+$/, '');
  const ext = outputMime === 'image/webp' ? 'webp' : 'jpg';

  try {
    for (const dimScale of DIMENSION_STEPS) {
      const fitScale = Math.min(1, maxDimension / Math.max(width, height));
      const scale = fitScale * dimScale;
      const targetW = Math.max(1, Math.round(width * scale));
      const targetH = Math.max(1, Math.round(height * scale));

      const canvas = document.createElement('canvas');
      canvas.width = targetW;
      canvas.height = targetH;
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;
      ctx.drawImage(source, 0, 0, targetW, targetH);

      for (const quality of QUALITY_STEPS) {
        const blob = await canvasToBlob(canvas, outputMime, quality);
        if (blob && blob.size <= maxBytes) {
          const compressed = new File([blob], `${baseName}.${ext}`, { type: outputMime });
          return { file: compressed, wasCompressed: true };
        }
      }
    }
    return null;
  } finally {
    release();
  }
}

function canSupportWebp(): boolean {
  try {
    const canvas = document.createElement('canvas');
    return canvas.toDataURL('image/webp').startsWith('data:image/webp');
  } catch {
    return false;
  }
}
